import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import logger from '../utils/logger';
import { useWordContext } from '../context/WordContext';
import './ChatbotDictionary.css';
import kanjiData from '../Languages/Japanese/kanjiDefinitions.json';

// Helper function to extract the specific sentence containing the target word
const extractSentenceContext = (fullText, targetWord) => {
  try {
    if (!fullText || !targetWord) return fullText;
    
    const targetIndex = fullText.indexOf(targetWord);
    
    if (targetIndex === -1) return fullText;
    
    // Find sentence boundaries (., !, ?)
    // Start by finding the beginning of the sentence
    let startIndex = targetIndex;
    while (startIndex > 0) {
      // Look for sentence-ending punctuation followed by space or start of text
      // For Japanese, include 。and ！and ？
      if (/[.!?。！？]/.test(fullText[startIndex - 1]) || startIndex === 1) {
        break;
      }
      startIndex--;
    }
    
    // If we found sentence-ending punctuation, adjust startIndex to include the next character
    if (startIndex > 0 && /[.!?。！？]/.test(fullText[startIndex - 1])) {
      // Don't include the punctuation in the new sentence
      startIndex = startIndex;
    } else if (startIndex === 0) {
      // If we've reached the beginning of the text
      startIndex = 0;
    }
    
    // Find the end of the sentence
    let endIndex = targetIndex + targetWord.length;
    while (endIndex < fullText.length) {
      // Look for sentence-ending punctuation
      // For Japanese, include 。and ！and ？
      if (/[.!?。！？]/.test(fullText[endIndex])) {
        endIndex++; // Include the punctuation
        break;
      }
      endIndex++;
    }
    
    // Get the complete sentence
    return fullText.substring(startIndex, endIndex).trim();
  } catch (err) {
    logger.error(`ChatbotKanjiLookup - Error extracting context for "${targetWord}":`, err);
    return fullText;
  }
};

// Define a retry mechanism for API calls
const MAX_RETRIES = 2;
const RETRY_DELAY = 1000;

const ChatbotKanjiLookup = ({ word, sentence, onSelectWord }) => {
  const [data, setData] = useState(null);
  const [contextData, setContextData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openAccordions, setOpenAccordions] = useState({
    context: false,
    grammar: false
  });
  const [openKanjiAccordions, setOpenKanjiAccordions] = useState({});
  const [wordGrade, setWordGrade] = useState(1);
  const [wordKnown, setWordKnown] = useState(false);
  const [kanjiGrades, setKanjiGrades] = useState({});
  const [kanjiKnownStatus, setKanjiKnownStatus] = useState({});
  const [justSavedWord, setJustSavedWord] = useState(false);
  const [justSavedKanji, setJustSavedKanji] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [apiFailure, setApiFailure] = useState(false);
  const [showWordGrading, setShowWordGrading] = useState(false);
  const [showKanjiGrading, setShowKanjiGrading] = useState({});

  // Log sentence parameter for debugging
  logger.debug(`ChatbotKanjiLookup initialized with word: "${word}", sentence: "${sentence || 'none'}"`);

  // Use the context instead of state and direct API calls
  const {
    isWordSaved,
    isKanjiSaved,
    addSavedWord,
    removeSavedWord,
    addSavedKanji,
    removeSavedKanji,
    getWordGrade,
    isWordKnown,
    getKanjiGrade,
    isKanjiKnown,
    updateWordGrade,
    updateWordKnown,
    updateKanjiGrade,
    updateKanjiKnown,
    refreshSavedWords
  } = useWordContext();

  // Get saved status from context
  const isSaved = isWordSaved(word, 'Japanese') || justSavedWord;
  
  // Get saved kanji list
  const savedKanjiList = Array.from(word)
    .filter(char => /[\u4E00-\u9FFF]/.test(char))
    .filter(char => isKanjiSaved(char) || justSavedKanji === char);

  // Initialize kanji grades and known status
  useEffect(() => {
    const newGrades = {};
    const newKnownStatus = {};

    Array.from(word)
      .filter(char => /[\u4E00-\u9FFF]/.test(char))
      .forEach(char => {
        if (isKanjiSaved(char)) {
          newGrades[char] = getKanjiGrade(char);
          newKnownStatus[char] = isKanjiKnown(char);
        } else {
          newGrades[char] = 1;
          newKnownStatus[char] = false;
        }
      });

    setKanjiGrades(newGrades);
    setKanjiKnownStatus(newKnownStatus);
    logger.debug('ChatbotKanjiLookup - Updated kanji grades:', newGrades);
    
    // Show grading UI if word is saved
    setShowWordGrading(isSaved);
  }, [word, isKanjiSaved, getKanjiGrade, isKanjiKnown, justSavedKanji, isSaved]);

  // Update local state when the word's saved status changes
  useEffect(() => {
    if (isSaved) {
      setWordGrade(getWordGrade(word, 'Japanese'));
      setWordKnown(isWordKnown(word, 'Japanese'));
      setShowWordGrading(true);
      logger.debug(`ChatbotKanjiLookup - Word "${word}" is saved with grade:`, getWordGrade(word, 'Japanese'));
    } else {
      setWordGrade(1);
      setWordKnown(false);
      setShowWordGrading(false);
    }
  }, [isSaved, word, getWordGrade, isWordKnown]);

  // Fetch data immediately when component mounts instead of waiting for click
  useEffect(() => {
    if (word) {
      fetchData();
    }
    
    // Cleanup function to cancel any pending operations when component unmounts
    return () => {
      // Cancel any pending operations if needed
      setLoading(false);
    };
  }, [word, sentence]);

  // Helper function to get userId from multiple sources
  const getUserId = () => {
    // Always try to get the latest userId from the JWT token first
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        // Decode JWT token to get user info
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        const decodedToken = JSON.parse(jsonPayload);
        if (decodedToken.sub) {
          const userId = decodedToken.sub;
          logger.debug(`ChatbotKanjiLookup - Extracted user ID from token: ${userId}`);
          // Update localStorage with the current userId from the token
          localStorage.setItem('userId', userId);
          return userId;
        }
      } catch (err) {
        logger.error("ChatbotKanjiLookup - Error decoding token:", err);
      }
    }

    // Fall back to localStorage only if token extraction fails
    const localUserId = localStorage.getItem('userId');
    if (localUserId) {
      logger.debug("ChatbotKanjiLookup - Using userId from localStorage (fallback)");
      return localUserId;
    }

    logger.warn("ChatbotKanjiLookup - No userId available from token or localStorage");
    return null;
  };

  // Helper function to create fake data for fallback when API fails
  const createFallbackData = () => {
    const hasKanji = Array.from(word).some(char => /[\u4E00-\u9FFF]/.test(char));
    
    return {
      data: [{
        japanese: [{ 
          word: word,
          reading: "Unavailable (Offline Mode)" 
        }],
        senses: [
          {
            english_definitions: ["Offline Mode - Definition unavailable"],
            parts_of_speech: ["Unavailable"]
          }
        ],
        jlpt: []
      }]
    };
  };

  // Helper function to handle retries for API calls
  const fetchWithRetry = async (apiCall, failureMessage, maxRetries = MAX_RETRIES) => {
    let retries = 0;
    
    while (retries <= maxRetries) {
      try {
        logger.debug(`ChatbotKanjiLookup - Retry ${retries}/${maxRetries} for API call`);
        const response = await apiCall();
        return response;
      } catch (err) {
        retries++;
        
        // Add detailed error logging
        logger.error(`API call failed (attempt ${retries}/${maxRetries + 1}):`, err);
        logger.error(`Error details:`, {
          message: err.message,
          stack: err.stack,
          name: err.name
        });
        
        if (retries > maxRetries) {
          logger.error(`All ${maxRetries + 1} attempts failed for API call. Last error:`, err);
          throw err;
        }
        
        // Wait between retries with exponential backoff
        const delay = RETRY_DELAY * Math.pow(2, retries - 1);
        logger.debug(`ChatbotKanjiLookup - Waiting ${delay}ms before retry`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };

  const fetchData = async () => {
    // If we already have data loaded, no need to fetch again
    if (data && data.word === word) {
      logger.debug(`ChatbotKanjiLookup - Using cached data for "${word}"`);
      return;
    }

    setLoading(true);
    setError(null);
    setApiFailure(false);
    
    try {
      // Extract the specific sentence containing the word if we have a full sentence
      const contextSentence = sentence ? extractSentenceContext(sentence, word) : null;
      
      logger.debug(`ChatbotKanjiLookup - Fetching data for word: "${word}", sentence context: "${contextSentence || 'none'}"`);
      const languageCode = localStorage.getItem('learningLanguage') || 'Japanese';

      // Use our backend proxy for Japanese lookups to avoid CORS issues
      let jishoData;
      try {
        const jishoResponse = await fetchWithRetry(
          async () => await apiClient.get(`/api/jisho?keyword=${encodeURIComponent(word)}`),
          "Failed to fetch from Jisho API"
        );

        if (!jishoResponse.ok) {
          logger.warn(`ChatbotKanjiLookup - Jisho API returned status ${jishoResponse.status}`);
          if (jishoResponse.status === 429) {
            setError("Rate limit exceeded. Please try again later.");
          } else {
            throw new Error(`Jisho API returned status ${jishoResponse.status}`);
          }
        }

        jishoData = await jishoResponse.json();
        
        // Add kanji info to the jishoData
        jishoData.kanjiInfo = {};
        
        // Extract kanji characters from the word
        const kanjiChars = extractKanjiFromWord(word);
        logger.debug(`ChatbotKanjiLookup - Found ${kanjiChars.length} kanji in word:`, kanjiChars);
        
        // Fetch details for each kanji
        if (kanjiChars.length > 0) {
          // We'll use Promise.all to fetch all kanji details concurrently
          const kanjiPromises = kanjiChars.map(async (kanjiChar) => {
            try {
              logger.debug(`ChatbotKanjiLookup - Fetching details for kanji: ${kanjiChar}`);
              const kanjiResponse = await fetchWithRetry(
                async () => await apiClient.get(`/api/jisho?keyword=${encodeURIComponent(kanjiChar + " #kanji")}`),
                `Failed to fetch details for kanji ${kanjiChar}`
              );
              
              if (!kanjiResponse.ok) {
                logger.warn(`ChatbotKanjiLookup - Kanji API returned status ${kanjiResponse.status} for ${kanjiChar}`);
                return null;
              }
              
              const kanjiData = await kanjiResponse.json();
              logger.debug(`ChatbotKanjiLookup - Got data for kanji ${kanjiChar}:`, kanjiData);
              return { kanjiChar, kanjiData };
            } catch (err) {
              logger.error(`Error fetching details for kanji ${kanjiChar}:`, err);
              return null;
            }
          });
          
          // Wait for all kanji requests to complete
          const kanjiResults = await Promise.all(kanjiPromises);
          
          // Process results and add to jishoData
          kanjiResults.forEach(result => {
            if (result) {
              const { kanjiChar, kanjiData } = result;
              
              // Extract meaningful kanji info from the response
              if (kanjiData && kanjiData.data && kanjiData.data.length > 0) {
                const entry = kanjiData.data[0];
                
                // Check if we have proper kanji data
                if (entry.jlpt || entry.grade) {
                  // Found proper kanji information
                  const meanings = entry.senses && entry.senses[0] ? 
                    entry.senses[0].english_definitions : ["No meaning available"];
                    
                  // Get readings - might need to extract from different parts
                  let onReadings = [];
                  let kunReadings = [];
                  
                  // Process Japanese readings
                  if (entry.japanese && entry.japanese.length > 0) {
                    // Usually on'yomi readings have katakana and kun'yomi have hiragana
                    entry.japanese.forEach(item => {
                      if (item.reading) {
                        // Check if reading is katakana (on'yomi) or hiragana (kun'yomi)
                        if (/[\u30A0-\u30FF]/.test(item.reading)) {
                          // Katakana - on'yomi
                          onReadings.push(item.reading);
                        } else {
                          // Hiragana - kun'yomi
                          kunReadings.push(item.reading);
                        }
                      }
                    });
                  }
                  
                  // Format JLPT level
                  let jlptLevel = "Unknown";
                  if (entry.jlpt && entry.jlpt.length > 0) {
                    jlptLevel = entry.jlpt[0].replace("jlpt-", "").toUpperCase();
                  }
                  
                  // Format grade
                  let grade = "Unknown";
                  if (entry.grade) {
                    grade = `Grade ${entry.grade}`;
                  }
                  
                  // Check frequency/commonness
                  let frequency = "Unknown";
                  if (entry.stroke_count) {
                    frequency = `${entry.stroke_count} strokes`;
                  }
                  
                  jishoData.kanjiInfo[kanjiChar] = {
                    meaning: meanings.join(', '),
                    onyomi: onReadings.length > 0 ? onReadings.join(', ') : "No on'yomi available",
                    kunyomi: kunReadings.length > 0 ? kunReadings.join(', ') : "No kun'yomi available",
                    grade: grade,
                    jlpt: jlptLevel,
                    freq: entry.is_common ? "Common" : frequency
                  };
                } else {
                  // Fall back to original processing if proper kanji data not found
                  jishoData.kanjiInfo[kanjiChar] = {
                    meaning: entry.senses && entry.senses[0] ? 
                      entry.senses[0].english_definitions.join(', ') : "No meaning available",
                    onyomi: entry.japanese && entry.japanese[0] && entry.japanese[0].reading ? 
                      entry.japanese[0].reading : "No on'yomi available",
                    kunyomi: entry.japanese && entry.japanese[1] && entry.japanese[1].reading ? 
                      entry.japanese[1].reading : "No kun'yomi available",
                    grade: entry.jlpt && entry.jlpt.length > 0 ? entry.jlpt[0] : "Unknown",
                    freq: entry.is_common ? "Common" : "Unknown"
                  };
                }
              }
            }
          });
          
          // For any kanji that didn't get populated from the API, use the static data
          kanjiChars.forEach(kanjiChar => {
            if (!jishoData.kanjiInfo[kanjiChar]) {
              // Use the imported kanjiData as fallback
              const staticDefinition = kanjiData.find(item => item.literal === kanjiChar);
              if (staticDefinition) {
                logger.debug(`ChatbotKanjiLookup - Using static data for kanji ${kanjiChar}:`, staticDefinition);
                
                // Map grade numbers to JLPT levels (rough approximation)
                let jlptLevel = null;
                if (staticDefinition.grade) {
                  const gradeNum = parseInt(staticDefinition.grade);
                  if (gradeNum <= 2) jlptLevel = "N5";
                  else if (gradeNum <= 4) jlptLevel = "N4";
                  else if (gradeNum <= 6) jlptLevel = "N3";
                  else if (gradeNum <= 8) jlptLevel = "N2";
                  else jlptLevel = "N1";
                }
                
                jishoData.kanjiInfo[kanjiChar] = {
                  meaning: staticDefinition.meanings ? staticDefinition.meanings.join(', ') : "No meaning available",
                  onyomi: staticDefinition.onReadings ? staticDefinition.onReadings.join(', ') : "No on'yomi available",
                  kunyomi: staticDefinition.kunReadings ? staticDefinition.kunReadings.join(', ') : "No kun'yomi available",
                  grade: staticDefinition.grade ? (staticDefinition.grade === "8" ? "Secondary School" : `Grade ${staticDefinition.grade}`) : "Unknown",
                  jlpt: jlptLevel,
                  frequency: staticDefinition.frequency ? `${staticDefinition.frequency} of 2500 most used kanji` : "Unknown"
                };
              }
            }
          });
        }
      } catch (err) {
        logger.error("Error fetching from Jisho API:", err);
        // Use fallback data if API fails
        jishoData = createFallbackData();
        setApiFailure(true);
      }
      
      setData(jishoData);
      logger.debug("Jisho data with kanji info:", jishoData);

      // If we have a sentence context, fetch contextual information
      if (sentence && !apiFailure) {
        logger.debug(`ChatbotKanjiLookup - Have sentence context: "${contextSentence}"`);
        
        try {
          // Call the context API for additional context information
          logger.info(`ChatbotKanjiLookup - Fetching context info for word "${word}" in sentence "${contextSentence}"`);
          
          // Extract a kanji character for better context information
          const kanjiChar = Array.from(word).find(char => /[\u4E00-\u9FFF]/.test(char)) || word;
          
          const contextResponse = await fetchWithRetry(
            async () => {
              const contextApiUrl = `/api/kanjiContext?word=${encodeURIComponent(word)}&kanji=${encodeURIComponent(kanjiChar)}&sentence=${encodeURIComponent(contextSentence)}`;
              return apiClient.get(contextApiUrl);
            },
            "Context API failed"
          );
          
          // Check if response is ok (status 200-299)
          if (!contextResponse.ok) {
            // Detailed error logging
            const statusText = contextResponse.statusText || '';
            let errorText = '';
            
            try {
              // Try to get response data as text
              errorText = await contextResponse.text().catch(() => '');
            } catch (textError) {
              logger.error("Could not get response text:", textError);
            }
            
            logger.error(`ChatbotKanjiLookup - Context API error status ${contextResponse.status} (${statusText}) for "${word}". Response: ${errorText}`);
            logger.error(`Context API error: ${contextResponse.status}. Status text: ${statusText}. Response: ${errorText}`);
            
            // Create fallback context data instead of throwing an error
            const fallbackContext = {
              context_note: `The word "${word}" appears in the sentence. (Server error - context analysis unavailable)`,
              grammar_note: "Grammar information unavailable at this time."
            };
            
            setContextData(fallbackContext);
            return; // Skip the rest of this block
          }

          // Parse response
          let contextResult;
          try {
            contextResult = await contextResponse.json();
          } catch (jsonError) {
            logger.error("Error parsing context response JSON:", jsonError);
            // Set fallback if JSON parsing fails
            contextResult = {
              context_note: `The word "${word}" appears in the context of "${contextSentence}". (JSON parsing error)`,
              grammar_note: "Grammar information unavailable due to parsing error."
            };
          }
          
          logger.info(`ChatbotKanjiLookup - Received context data for "${word}":`, contextResult);
          logger.debug("Context data:", contextResult);
          setContextData(contextResult);
        } catch (err) {
          // Enhanced error logging
          logger.error("Error fetching context data:", err);
          logger.error("Error details:", {
            message: err.message,
            stack: err.stack,
            name: err.name
          });
          
          // Create fallback context data if API call fails
          setContextData({
            context_note: `The word "${word}" appears in the context of "${contextSentence}". (Network error - using offline mode)`,
            grammar_note: "Grammar information unavailable in offline mode."
          });
        }
      } else {
        logger.debug("ChatbotKanjiLookup - No sentence context available or API already failed");
        if (sentence) {
          setContextData({
            context_note: `The word "${word}" appears in the context of "${sentence}". (Offline mode)`,
            grammar_note: "Grammar information unavailable in offline mode."
          });
        } else {
          setContextData(null);
        }
      }
    } catch (err) {
      logger.error("ChatbotKanjiLookup - Error in fetch operation:", err);
      logger.error("Error in fetch operation:", err);
      setError(err.message || "Failed to fetch definition");
      
      // Always provide fallback data
      if (!data) {
        setData(createFallbackData());
      }
      
      // If we have a sentence but failed to get context, create a fallback context
      if (sentence && !contextData) {
        logger.debug("Creating fallback context data");
        setContextData({
          context_note: `The word "${word}" appears in the context of "${sentence}". (Unable to connect to server)`,
          grammar_note: "Grammar information requires the server API to be running properly."
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Toggle accordion for context and grammar sections
  const toggleAccordion = (section) => {
    setOpenAccordions(prev => {
      // Create a new state with all sections closed
      const newState = {
        context: false,
        grammar: false
      };
      
      // Only toggle the selected section if it's not already open
      if (!prev[section]) {
        newState[section] = true;
      }
      
      return newState;
    });
  };

  // Toggle kanji accordion open/close state
  const toggleKanjiAccordion = (kanjiChar) => {
    // Close all accordions and only open the selected one
    const newOpenKanjiAccordions = {};
    
    // If the clicked accordion was already open, close all of them
    // Otherwise, only open the clicked one
    if (openKanjiAccordions[kanjiChar]) {
      // Already open, so close all
      setOpenKanjiAccordions({});
    } else {
      // Open only the clicked one
      newOpenKanjiAccordions[kanjiChar] = true;
      setOpenKanjiAccordions(newOpenKanjiAccordions);
    }
  };

  const handleSaveWord = async (e) => {
    if (e) e.stopPropagation();
    
    const userId = getUserId();
    
    if (!userId) {
      logger.error("ChatbotKanjiLookup - No user ID available, cannot save word");
      setError("No user ID available. Please log in again.");
      return;
    }

    try {
      logger.debug(`ChatbotKanjiLookup - Saving word "${word}" with userId ${userId}`);
      
      // Make direct API call like in KanjiLookup.js instead of just using context
      const result = await apiClient.post('/api/saveUserWord', {
        userId,
        language: 'Japanese',
        item: word,
        grade: 1,
        known: false
      });

      // Check if result is valid (apiClient.post now returns the parsed JSON directly)
      if (!result || !result.message) {
        logger.error("ChatbotKanjiLookup - Failed to save word: Invalid response");
        throw new Error("Failed to save word: Invalid response");
      }

      logger.debug("ChatbotKanjiLookup - Word saved successfully:", result.message);
      
      // Update context
      addSavedWord(word, 'Japanese');
      
      // Update UI with animation
      setJustSavedWord(true);
      setTimeout(() => setJustSavedWord(false), 2000);

      // Show grading UI
      setShowWordGrading(true);
      
      // Force refresh to update all saved words
      refreshSavedWords();
      
      // Set default grade and known state
      setWordGrade(1);
      setWordKnown(false);
      
      // Dispatch an event to notify that word data has been updated
      window.dispatchEvent(new CustomEvent('wordDataRefreshed'));
      
      logger.debug(`ChatbotKanjiLookup - Saved word "${word}" successfully`);
    } catch (err) {
      logger.error("ChatbotKanjiLookup - Error saving word:", err);
      logger.error("Error saving word:", err);
      setError("Failed to save word. Please try again.");
    }
  };

  const handleDeleteWord = async (e) => {
    if (e) e.stopPropagation();
    
    const userId = getUserId();
    
    if (!userId) {
      logger.error("ChatbotKanjiLookup - No user ID available, cannot delete word");
      setError("No user ID available. Please log in again.");
      return;
    }

    try {
      logger.debug(`ChatbotKanjiLookup - Deleting word "${word}" with userId ${userId}`);
      
      // Make direct API call instead of just using context
      const result = await apiClient.post('/api/removeUserWord', {
        userId,
        language: 'Japanese',
        item: word
      });

      // Check if result is valid (apiClient.post now returns the parsed JSON directly)
      if (!result || !result.message) {
        logger.error("ChatbotKanjiLookup - Failed to delete word: Invalid response");
        throw new Error("Failed to delete word: Invalid response");
      }

      logger.debug("ChatbotKanjiLookup - Word deleted successfully:", result.message);
      
      // Update context
      removeSavedWord(word, 'Japanese');
      
      // Update UI
      setJustSavedWord(false);
      setShowWordGrading(false);
      
      // Force refresh to update all saved words
      refreshSavedWords();
      
      // Dispatch an event to notify that word data has been updated
      window.dispatchEvent(new CustomEvent('wordDataRefreshed'));
      
      logger.debug(`ChatbotKanjiLookup - Deleted word "${word}" successfully`);
    } catch (err) {
      logger.error("ChatbotKanjiLookup - Error deleting word:", err);
      logger.error("Error deleting word:", err);
      setError("Failed to delete word. Please try again.");
    }
  };

  const handleGradeChange = async (e) => {
    const newGrade = parseInt(e.target.value, 10);
    setWordGrade(newGrade);
    
    if (isSaved) {
      try {
        await updateWordGrade(word, 'Japanese', newGrade);
        logger.debug(`ChatbotKanjiLookup - Updated grade for "${word}" to ${newGrade}`);
      } catch (err) {
        logger.error("ChatbotKanjiLookup - Error updating word grade:", err);
        logger.error("Error updating word grade:", err);
      }
    }
  };

  const handleKnownChange = async (e) => {
    const newKnownStatus = e.target.checked;
    setWordKnown(newKnownStatus);
    
    if (isSaved) {
      try {
        await updateWordKnown(word, 'Japanese', newKnownStatus);
        logger.debug(`ChatbotKanjiLookup - Updated known status for "${word}" to ${newKnownStatus}`);
      } catch (err) {
        logger.error("ChatbotKanjiLookup - Error updating word known status:", err);
        logger.error("Error updating word known status:", err);
      }
    }
  };

  const handleSaveKanji = async (kanji) => {
    const userId = getUserId();
    
    if (!userId) {
      logger.error("ChatbotKanjiLookup - No user ID available, cannot save kanji");
      setError("No user ID available. Please log in again.");
      return;
    }

    try {
      logger.debug(`ChatbotKanjiLookup - Saving kanji "${kanji}" with userId ${userId}`);
      
      // Make direct API call
      const result = await apiClient.post('/api/saveUserKanji', {
        userId,
        kanji: kanji,
        grade: 1,
        known: false,
        type: 'kanji'
      });

      // Check if result is valid (apiClient.post now returns the parsed JSON directly)
      if (!result || !result.message) {
        logger.error("ChatbotKanjiLookup - Failed to save kanji: Invalid response");
        throw new Error("Failed to save kanji: Invalid response");
      }

      logger.debug("ChatbotKanjiLookup - Kanji saved successfully:", result.message);
      
      // Update context
      addSavedKanji(kanji);
      
      // Update local state
      setJustSavedKanji(kanji);
      setTimeout(() => setJustSavedKanji(null), 2000);
      setKanjiGrades(prev => ({...prev, [kanji]: 1}));
      setKanjiKnownStatus(prev => ({...prev, [kanji]: false}));
      
      // Update UI
      const newKanjiDict = {...showKanjiGrading};
      newKanjiDict[kanji] = true;
      setShowKanjiGrading(newKanjiDict);
      
      // Force refresh
      refreshSavedWords();
      
      // Dispatch an event to notify that word data has been updated
      window.dispatchEvent(new CustomEvent('wordDataRefreshed'));
      
      logger.debug(`ChatbotKanjiLookup - Saved kanji "${kanji}" successfully`);
    } catch (err) {
      logger.error("ChatbotKanjiLookup - Error saving kanji:", err);
      logger.error("Error saving kanji:", err);
      setError("Failed to save kanji. Please try again.");
    }
  };

  const handleDeleteKanji = async (kanji) => {
    const userId = getUserId();

    if (!userId) {
      logger.error("ChatbotKanjiLookup - No user ID available, cannot delete kanji");
      setError("No user ID available. Please log in again.");
      return;
    }

    try {
      logger.debug(`ChatbotKanjiLookup - Deleting kanji "${kanji}" with userId ${userId}`);
      
      const result = await apiClient.post('/api/deleteUserKanji', {
        userId,
        kanji: kanji
      });

      // Check if result is valid (apiClient.post now returns the parsed JSON directly)
      if (!result || !result.message) {
        logger.error("ChatbotKanjiLookup - Failed to delete kanji: Invalid response");
        throw new Error("Failed to delete kanji: Invalid response");
      }

      logger.debug("ChatbotKanjiLookup - Kanji deleted successfully:", result.message);
      
      // Update context
      removeSavedKanji(kanji);
      
      // Update UI state
      if (justSavedKanji === kanji) {
        setJustSavedKanji(null);
      }
      
      // Hide grading UI for this kanji
      setShowKanjiGrading(prev => ({
        ...prev,
        [kanji]: false
      }));
      
      // Force refresh
      refreshSavedWords();
      
      // Dispatch an event to notify that word data has been updated
      window.dispatchEvent(new CustomEvent('wordDataRefreshed'));
      
      logger.debug(`ChatbotKanjiLookup - Deleted kanji "${kanji}" successfully`);
    } catch (err) {
      logger.error("ChatbotKanjiLookup - Error deleting kanji:", err);
      logger.error("Error deleting kanji:", err);
      setError("Failed to delete kanji. Please try again.");
    }
  };

  const handleKanjiGradeChange = async (kanjiChar, e) => {
    const newGrade = parseInt(e.target.value, 10);
    
    setKanjiGrades(prev => ({
      ...prev,
      [kanjiChar]: newGrade
    }));
    
    if (isKanjiSaved(kanjiChar)) {
      try {
        const success = await updateKanjiGrade(kanjiChar, newGrade);
        if (!success) {
          logger.error("ChatbotKanjiLookup - Failed to update kanji grade");
          setError("Failed to update kanji grade. Please try again.");
        } else {
          logger.debug(`ChatbotKanjiLookup - Updated grade for kanji "${kanjiChar}" to ${newGrade}`);
        }
      } catch (err) {
        logger.error("ChatbotKanjiLookup - Error updating kanji grade:", err);
        setError("Failed to update kanji grade. Please try again.");
      }
    }
  };

  const handleKanjiKnownChange = async (kanjiChar, e) => {
    const newKnownStatus = e.target.checked;
    
    setKanjiKnownStatus(prev => ({
      ...prev,
      [kanjiChar]: newKnownStatus
    }));
    
    if (isKanjiSaved(kanjiChar)) {
      try {
        const success = await updateKanjiKnown(kanjiChar, newKnownStatus);
        if (!success) {
          logger.error("ChatbotKanjiLookup - Failed to update kanji known status");
          setError("Failed to update kanji status. Please try again.");
        } else {
          logger.debug(`ChatbotKanjiLookup - Updated known status for kanji "${kanjiChar}" to ${newKnownStatus}`);
        }
      } catch (err) {
        logger.error("ChatbotKanjiLookup - Error updating kanji known status:", err);
        setError("Failed to update kanji status. Please try again.");
      }
    }
  };

  const renderWordGradingUI = () => {
    return (
      <div className="chatbot-grading">
        <div className="difficulty-scale">
          <div className="difficulty-labels">
            <span className="difficulty-hard">Hard</span>
            <span className="difficulty-easy">Easy</span>
          </div>
          <div className="radio-group">
            {[1, 2, 3, 4, 5].map(grade => (
              <label key={grade} className={`radio-option radio-grade-${grade} ${wordGrade === grade ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="wordGrade"
                  value={grade}
                  checked={wordGrade === grade}
                  onChange={handleGradeChange}
                />
                <span>{grade}</span>
              </label>
            ))}
          </div>
        </div>
        
        <div className="known-checkbox">
          <input
            type="checkbox"
            id="wordKnown"
            checked={wordKnown}
            onChange={handleKnownChange}
          />
          <div className="known-checkbox-info">
            <label htmlFor="wordKnown" className="known-checkbox-label">
              I know this word
            </label>
            <div className="known-checkbox-description">
              Words marked as known won't be highlighted in texts
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderKanjiGradingUI = (kanjiChar) => {
    const grade = kanjiGrades[kanjiChar] || 1;
    const isKnown = kanjiKnownStatus[kanjiChar] || false;
    
    return (
      <div className="chatbot-grading">
        <div className="difficulty-scale">
          <div className="difficulty-labels">
            <span className="difficulty-hard">Hard</span>
            <span className="difficulty-easy">Easy</span>
          </div>
          <div className="radio-group">
            {[1, 2, 3, 4, 5].map(gradeValue => (
              <label key={gradeValue} className={`radio-option radio-grade-${gradeValue} ${grade === gradeValue ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name={`kanjiGrade_${kanjiChar}`}
                  value={gradeValue}
                  checked={grade === gradeValue}
                  onChange={(e) => handleKanjiGradeChange(kanjiChar, e)}
                />
                <span>{gradeValue}</span>
              </label>
            ))}
          </div>
        </div>
        
        <div className="known-checkbox">
          <input
            type="checkbox"
            id={`kanjiKnown_${kanjiChar}`}
            checked={isKnown}
            onChange={(e) => handleKanjiKnownChange(kanjiChar, e)}
          />
          <div className="known-checkbox-info">
            <label htmlFor={`kanjiKnown_${kanjiChar}`} className="known-checkbox-label">
              I know this kanji
            </label>
            <div className="known-checkbox-description">
              Kanji marked as known won't be highlighted in texts
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Get CSS class for a kanji character based on its saved state and grade
  const getKanjiClass = (kanjiChar) => {
    const baseClass = "chatbot-clickable-kanji";
    
    if (isKanjiSaved(kanjiChar) || justSavedKanji === kanjiChar) {
      const grade = kanjiGrades[kanjiChar] || 1;
      return `${baseClass} chatbot-saved-kanji chatbot-grade-${grade}`;
    }
    
    return baseClass;
  };

  // Get CSS class for the word based on its saved state and grade
  const getWordClass = () => {
    // This getWordClass is only for the word heading in the definition panel
    const baseClass = "chatbot-word-heading";
    
    if (isSaved) {
      return `${baseClass} chatbot-saved-word chatbot-grade-${wordGrade}`;
    }
    
    return baseClass;
  };

  // Extract only kanji from the word
  const extractKanjiFromWord = (word) => {
    return Array.from(word).filter(char => /[\u4E00-\u9FFF]/.test(char));
  };

  // Get kanji details from kanjiData
  const getKanjiDetails = (kanjiChar, kanjiData = {}) => {
    // First try to get from API data if available
    if (data && data.kanjiInfo && data.kanjiInfo[kanjiChar]) {
      const apiDetails = data.kanjiInfo[kanjiChar];
      return {
        meaning: apiDetails.meaning || "No meaning available",
        onyomi: apiDetails.onyomi || "No on'yomi available",
        kunyomi: apiDetails.kunyomi || "No kun'yomi available",
        grade: apiDetails.grade || "Unknown",
        jlpt: apiDetails.jlpt || null,
        frequency: apiDetails.freq || "Unknown"
      };
    }
    
    // Otherwise, use the imported kanjiData (checking if it's an array first)
    if (Array.isArray(kanjiData)) {
      const definition = kanjiData.find((item) => item.literal === kanjiChar);
      if (definition) {
        // Map grade numbers to JLPT levels (rough approximation)
        let jlptLevel = null;
        if (definition.grade) {
          const gradeNum = parseInt(definition.grade);
          if (gradeNum <= 2) jlptLevel = "N5";
          else if (gradeNum <= 4) jlptLevel = "N4";
          else if (gradeNum <= 6) jlptLevel = "N3";
          else if (gradeNum <= 8) jlptLevel = "N2";
          else jlptLevel = "N1";
        }
        
        return {
          meaning: definition.meanings ? definition.meanings.join(', ') : "No meaning available",
          onyomi: definition.onReadings ? definition.onReadings.join(', ') : "No on'yomi available",
          kunyomi: definition.kunReadings ? definition.kunReadings.join(', ') : "No kun'yomi available",
          grade: definition.grade ? (definition.grade === "8" ? "Secondary School" : `Grade ${definition.grade}`) : "Unknown",
          jlpt: jlptLevel,
          frequency: definition.frequency ? `${definition.frequency} of 2500 most used kanji` : "Unknown"
        };
      }
    }
    
    // Fallback if neither source has the data
    return {
      meaning: "No meaning available",
      onyomi: "No on'yomi available",
      kunyomi: "No kun'yomi available",
      grade: "Unknown",
      jlpt: null,
      frequency: "Unknown"
    };
  };
  
  // Retry button to manually retry loading when API fails
  const handleRetry = () => {
    setError(null);
    setRetryCount(prev => prev + 1);
    fetchData();
  };

  // This function tells the parent how to style the word in the text
  useEffect(() => {
    if (onSelectWord) {
      // Create style info for the parent component to highlight the word in text
      const wordStyleInfo = {
        word: word,
        isSaved: isSaved,
        grade: wordGrade,
        known: wordKnown,
        className: isSaved ? `chatbot-clickable-word chatbot-saved-word chatbot-grade-${wordGrade}` : "chatbot-clickable-word"
      };
      
      // Create style info for individual kanji characters
      const kanjiStyleInfo = {};
      
      // Extract kanji from the word and determine styling for each
      const kanjiChars = extractKanjiFromWord(word);
      kanjiChars.forEach(kanjiChar => {
        const isKanjiSavedStatus = isKanjiSaved(kanjiChar) || justSavedKanji === kanjiChar;
        const grade = kanjiGrades[kanjiChar] || 1;
        const known = kanjiKnownStatus[kanjiChar] || false;
        
        kanjiStyleInfo[kanjiChar] = {
          isSaved: isKanjiSavedStatus,
          grade: grade,
          known: known,
          className: isKanjiSavedStatus ? 
            `chatbot-clickable-kanji chatbot-saved-kanji chatbot-grade-${grade}` : 
            "chatbot-clickable-kanji"
        };
      });
      
      // Send both word and kanji style info to the parent
      onSelectWord(word, {
        word: wordStyleInfo,
        kanji: kanjiStyleInfo
      });
    }
  }, [
    word, isSaved, wordGrade, wordKnown, 
    kanjiGrades, kanjiKnownStatus, justSavedWord, justSavedKanji,
    isKanjiSaved, onSelectWord
  ]);

  // Main render function
  const renderContent = () => {
    if (loading) {
      return <div className="chatbot-loading">Loading...</div>;
    }

    if (error) {
      return (
        <div className="chatbot-error-container">
          <div className="chatbot-error-text">Error: {error}</div>
          <button className="chatbot-retry-btn" onClick={handleRetry}>
            Retry
          </button>
          <div className="chatbot-error-note">
            Note: The server appears to be experiencing issues. Please check your backend connection.
          </div>
        </div>
      );
    }

    if (!data) {
      return <div className="chatbot-loading">No data available.</div>;
    }

    // Debug information - display in development mode only
    const showDebugInfo = window.process?.env?.NODE_ENV === 'development';
    
    // Check if we have valid data from Jisho
    if (!data.data || data.data.length === 0) {
      return (
        <div className="chatbot-error-container">
          <div className="chatbot-error-text">No dictionary entries found for "{word}".</div>
          <button className="chatbot-retry-btn" onClick={handleRetry}>
            Retry
          </button>
          {showDebugInfo && (
            <div className="chatbot-debug-info">
              <h4>Debug Info:</h4>
              <pre>{JSON.stringify({word, sentence, contextData}, null, 2)}</pre>
            </div>
          )}
        </div>
      );
    }

    // Get only the first Japanese entry for the main word
    const wordData = data.data.find(item => 
      item.japanese && 
      item.japanese[0] && 
      item.japanese[0].word && 
      item.japanese[0].word === word
    ) || data.data[0];

    const readings = wordData.japanese?.[0]?.reading || '';
    const wordWriting = wordData.japanese?.[0]?.word || word;
    const jlpt = wordData.jlpt || [];
    
    // Extract kanji chars from the word
    const kanjiChars = extractKanjiFromWord(wordWriting);
    
    return (
      <div className="chatbot-dictionary-word-definition">
        {loading ? (
          <div className="chatbot-loading">
            <div className="chatbot-loading-spinner"></div>
            <p>Loading definition...</p>
          </div>
        ) : error ? (
          <div className="chatbot-error-container">
            <div className="chatbot-error-text">{error}</div>
            <div className="chatbot-error-note">
              <p>Unable to fetch definition. Please try again later.</p>
              <button 
                className="chatbot-retry-btn" 
                onClick={handleRetry}
              >
                Retry
              </button>
            </div>
          </div>
        ) : data ? (
          <>
            {/* Word heading section */}
            <div className={getWordClass()}>
              <span>
                {wordData?.japanese[0]?.word || word}
              </span>
              {wordData?.japanese[0]?.reading && (
                <span className="chatbot-reading"> 【{wordData.japanese[0].reading}】</span>
              )}
            </div>
            
            {/* Word definition content */}
            <div className="chatbot-definition-content">
              {wordData.senses.slice(0, 2).filter(sense => 
                // Filter out Wikipedia definitions
                !(sense.parts_of_speech && sense.parts_of_speech.includes('Wikipedia definition'))
              ).map((sense, index) => (
                <div key={index} className="chatbot-definition-section">
                  {/* Part of speech */}
                  {sense.parts_of_speech && sense.parts_of_speech.length > 0 && (
                    <div className="chatbot-word-type">
                      {sense.parts_of_speech.join(', ')}
                    </div>
                  )}
                  
                  {/* Definitions list */}
                  <ul>
                    {sense.english_definitions.map((def, i) => (
                      <li key={i}>{def}</li>
                    ))}
                  </ul>
                  {index < Math.min(1, wordData.senses.length - 1) && (
                    <hr className="chatbot-definition-separator" />
                  )}
                </div>
              ))}
            </div>
            
            {/* Context accordion - ONLY in main word definition section */}
            {contextData && contextData.context_note && (
              <div className="chatbot-definition-accordion">
                <div 
                  className="chatbot-definition-accordion-header" 
                  onClick={() => toggleAccordion('context')}
                >
                  <h4 className="chatbot-definition-accordion-title">Context</h4>
                  <span className="chatbot-definition-accordion-icon">
                    {openAccordions.context ? '▼' : '►'}
                  </span>
                </div>
                {openAccordions.context && (
                  <div className="chatbot-definition-accordion-content">
                    <div className="chatbot-context-note">
                      <p>{contextData.context_note}</p>
                      {apiFailure && (
                        <button 
                          className="chatbot-refresh-btn" 
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchData();
                          }}
                        >
                          Refresh Context
                        </button>
                      )}
                      {showDebugInfo && (
                        <div className="chatbot-debug-info" style={{marginTop: '10px', fontSize: '12px'}}>
                          <details>
                            <summary>Debug Info</summary>
                            <div>
                              <strong>Word:</strong> {word}<br/>
                              <strong>Contains Kanji:</strong> {Array.from(word).some(c => /[\u4E00-\u9FFF]/.test(c)) ? 'Yes' : 'No'}<br/>
                              <strong>Kanji Chars:</strong> {Array.from(word).filter(c => /[\u4E00-\u9FFF]/.test(c)).join(', ') || 'None'}<br/>
                              <strong>API Failure:</strong> {apiFailure ? 'Yes' : 'No'}<br/>
                              <strong>Context Data Source:</strong> {apiFailure ? 'Local Fallback' : 'Server API'}<br/>
                            </div>
                          </details>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Grammar accordion - ONLY in main word definition section */}
            {contextData && contextData.grammar_note && (
              <div className="chatbot-definition-accordion">
                <div 
                  className="chatbot-definition-accordion-header" 
                  onClick={() => toggleAccordion('grammar')}
                >
                  <h4 className="chatbot-definition-accordion-title">Grammar</h4>
                  <span className="chatbot-definition-accordion-icon">
                    {openAccordions.grammar ? '▼' : '►'}
                  </span>
                </div>
                {openAccordions.grammar && (
                  <div className="chatbot-definition-accordion-content">
                    <div className="chatbot-grammar-note">
                      <p>{contextData.grammar_note}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Save/Delete word buttons */}
            <div className="chatbot-word-buttons">
              {!isSaved ? (
                <button 
                  className="chatbot-save-btn" 
                  onClick={handleSaveWord}
                >
                  Save Word
                </button>
              ) : (
                <button 
                  className="chatbot-delete-btn" 
                  onClick={handleDeleteWord}
                >
                  Delete Word
                </button>
              )}
            </div>
            
            {/* Word grading UI */}
            {showWordGrading && renderWordGradingUI()}
            
            {/* Individual kanji components - NO context/grammar accordions here */}
            {kanjiChars.length > 0 && (
              <div className="chatbot-kanji-components-section">
                <h4 className="chatbot-kanji-components-title">Kanji Components</h4>
                
                {kanjiChars.map((kanjiChar, index) => {
                  const isKanjiSavedStatus = isKanjiSaved(kanjiChar) || justSavedKanji === kanjiChar;
                  const showKanjiGradingUI = showKanjiGrading[kanjiChar] || isKanjiSavedStatus;
                  
                  return (
                    <div className="chatbot-kanji-item" key={`kanji-${index}`}>
                      {/* Kanji accordion header */}
                      <div 
                        className="chatbot-definition-accordion-header" 
                        onClick={() => toggleKanjiAccordion(kanjiChar)}
                      >
                        <h4 className="chatbot-definition-accordion-title">
                          <span className={getKanjiClass(kanjiChar)}>{kanjiChar}</span>
                        </h4>
                        <span className="chatbot-definition-accordion-icon">
                          {openKanjiAccordions[kanjiChar] ? '▼' : '►'}
                        </span>
                      </div>
                      
                      {/* Kanji accordion content - NO context/grammar accordions here */}
                      {openKanjiAccordions[kanjiChar] && (
                        <div className="chatbot-definition-accordion-content">
                          <div className="chatbot-kanji-details">
                            {/* Kanji info from Jisho */}
                            <div className="chatbot-kanji-info">
                              {(() => {
                                const details = getKanjiDetails(kanjiChar);
                                return (
                                  <>
                                    <div className="chatbot-kanji-meaning">
                                      <strong>Meaning:</strong> {details.meaning}
                                    </div>
                                    <div className="chatbot-kanji-onyomi">
                                      <strong>On Reading:</strong> {details.onyomi}
                                    </div>
                                    <div className="chatbot-kanji-kunyomi">
                                      <strong>Kun Reading:</strong> {details.kunyomi}
                                    </div>
                                    <div className="chatbot-kanji-grade">
                                      <strong>Grade:</strong> {details.grade}
                                    </div>
                                    {details.jlpt && (
                                      <div className="chatbot-kanji-jlpt">
                                        <strong>JLPT:</strong> {details.jlpt}
                                      </div>
                                    )}
                                    <div className="chatbot-kanji-frequency">
                                      <strong>Frequency:</strong> {details.frequency}
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                            
                            {/* Save/Delete kanji buttons */}
                            <div className="chatbot-word-buttons">
                              {!isKanjiSavedStatus ? (
                                <button 
                                  className="chatbot-save-btn" 
                                  onClick={() => handleSaveKanji(kanjiChar)}
                                >
                                  Save Kanji
                                </button>
                              ) : (
                                <button 
                                  className="chatbot-delete-btn" 
                                  onClick={() => handleDeleteKanji(kanjiChar)}
                                >
                                  Delete Kanji
                                </button>
                              )}
                            </div>
                            
                            {/* Kanji grading UI */}
                            {showKanjiGradingUI && renderKanjiGradingUI(kanjiChar)}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <div className="chatbot-dictionary-empty">
            <p>No definition found for "{word}"</p>
          </div>
        )}
      </div>
    );
  };

  return (
    renderContent()
  );
};

export default ChatbotKanjiLookup; 